import { IObservable, IObserver, Observable } from '@1js/oil-externals/rx';
import { OfficeRoute } from '../../shared/constants/office-route.enum';
import { KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { FileType } from '../../shared/models/file-item.model';
import { HttpRequestOptions } from '../../shared/models/http-request-options.model';
import { AuthType } from '../../shared/shared-context/client-startup-config.service';
import { SearchInstrumentedXhrService } from '../search-instrumented-xhr.service';
import { BaseSuggestionProvider } from './base.search-suggestion-provider';

/*
 * Data provider for TellMe service call.
 */
export abstract class TellMeSearchSuggestionProvider extends BaseSuggestionProvider {

    public static debounceTimeoutId: number;
    public static searchInstrumentedXhrService: SearchInstrumentedXhrService;
    public static currentSearchInfo: string;
    public static suggestionPromise: Promise<Object>;
    private readonly tellMeServiceEndPoint: string = 'https://tellmeservice.osi.office.net/tellmeservice/api/suggestions';
    private readonly debounceWaitTime: number = 200;
    private readonly maxCharacterLength: number = 256;
    private readonly searchTextUsedInVisualTesting: string = 'testing';

    public createStream(searchText: string): IObservable<any> {
        if (searchText?.trim() !== '') {
            if (TellMeSearchSuggestionProvider.clientStartupConfigService.config.authType === AuthType.MSA || TellMeSearchSuggestionProvider.officeRouteService.getCurrentRoute().route === OfficeRoute.Create) {
                return Observable.create(async (observer: IObserver<any>) => {
                    if (BaseSuggestionProvider.contextService.sharedContext.inMockMode && searchText === this.searchTextUsedInVisualTesting ) {
                        this.getMockedSearchSuggestion(searchText, observer);
                    } else if (searchText === TellMeSearchSuggestionProvider.currentSearchInfo) {
                        this.getSearchSuggestion(searchText, await TellMeSearchSuggestionProvider.suggestionPromise, observer);
                    } else {
                        TellMeSearchSuggestionProvider.currentSearchInfo = searchText;

                        window.clearTimeout(TellMeSearchSuggestionProvider.debounceTimeoutId);
                        TellMeSearchSuggestionProvider.debounceTimeoutId = null;

                        const suggestionPromise: Promise<Object> = new Promise(resolve => {
                            TellMeSearchSuggestionProvider.debounceTimeoutId = null;
                            TellMeSearchSuggestionProvider.debounceTimeoutId = window.setTimeout(() => {
                                resolve(this.createRequestPromise(searchText));
                            }, this.debounceWaitTime);
                        });

                        TellMeSearchSuggestionProvider.suggestionPromise = suggestionPromise;

                        const suggestions = await suggestionPromise;

                        this.getSearchSuggestion(searchText, suggestions, observer);
                    }
                });
            }
        }
        return Observable.empty();
    }

    public createRequestPromise(searchText: string): Promise<Object> {
        if (searchText.length > this.maxCharacterLength) {
            searchText = searchText.substring(0, this.maxCharacterLength);
        }

        const requestHeader: Map<string, string> = new Map<string, string>();
        requestHeader.set('Content-Type', 'application/json');
        requestHeader.set('Accept', 'application/json');
        requestHeader.set('X-Office-Application', TellMeSearchSuggestionProvider.capabilitiesService.capabilities.isPwaInUwp ? 'PWAMSA' : 'OfficeHomeMSA');
        requestHeader.set('X-Office-Platform', 'Web');
        requestHeader.set('X-TellMe-SuggestionTypes', 'Help,Template');
        requestHeader.set('X-TellMe-Culture', TellMeSearchSuggestionProvider.contextService.sharedContext.officeMarket);
        requestHeader.set('X-TellMe-SessionId', TellMeSearchSuggestionProvider.searchInstrumentationService.getSearchSessionId());

        const request: HttpRequestOptions = {
            url: this.tellMeServiceEndPoint,
            headers: requestHeader
        };

        const requestBody = {'Query': searchText, 'TemplateInfo': {'Application' : this.getApplicationList(TellMeSearchSuggestionProvider.currentRoute.app)}};

        const searchSuggestionPromise: Promise<Object> = TellMeSearchSuggestionProvider.searchInstrumentedXhrService.sendIntrumentedPostRequest(
            KnownInstrumentationEvents.TellMeSearchSuggestionRequest,
            this.tellMeServiceEndPoint,
            requestBody,
            request
        );

        return searchSuggestionPromise;
    }

    public abstract getId(): string;

    public abstract getSearchSuggestion(searchText: string, searchSuggestion: Object, observer: IObserver<any>);

    public abstract getMockedSearchSuggestion(searchText: string, observer: IObserver<any>);

    private getApplicationList(fileType: FileType): string {
        switch (fileType) {
            case FileType.Word:
                return 'Word';
            case FileType.Excel:
                return 'Excel';
            case FileType.PowerPoint:
                return 'PowerPoint';
            default:
                return 'Word,PowerPoint,Excel';
        }
    }
}
